import { DataTableColumn } from '../../../shared/ui/data-table/data-table.component';

/**
 * The columns of the Leads list, in display order.
 *
 * Lifted out of the Leads Board so the Territories page can put the SAME table
 * under a patch rather than grow a second, thinner one beside it. That second
 * one is what existed: three columns - name, address, pinned - against these
 * sixteen, so a rep redrawing a boundary could not see the stage, the rep, the
 * contribution or the next action of the doors they were moving without
 * leaving the map and finding each lead again on /leads/table.
 *
 * Ids are the query contract: `sortKey` is what the server sorts on, and the
 * ids double as the keys a page's `dtCell` templates and the column picker's
 * stored preferences are written against. Renaming one resets that user's
 * choice, which is why the labels move and the ids do not.
 */
export const LEAD_LIST_COLUMNS: readonly DataTableColumn[] = [
  { id: 'title', label: 'Title' },
  { id: 'address', label: 'Address', sortable: false },
  { id: 'city', label: 'City', sortable: false },
  { id: 'state', label: 'State', sortable: false },
  { id: 'postalCode', label: 'ZIP', sortable: false },
  { id: 'boardColumn', label: 'Stage' },
  { id: 'origin', label: 'Origin' },
  { id: 'sources', label: 'Sources', sortKey: 'sourceCount' },
  { id: 'businessTypes', label: 'Types', sortable: false },
  { id: 'salesRep', label: 'Sales rep', sortable: false },
  { id: 'score', label: 'Score', sortKey: 'qualificationScore' },
  { id: 'contribution', label: 'Contribution', sortKey: 'contributionBase' },
  { id: 'routeFit', label: 'Route fit', sortable: false },
  { id: 'nextAction', label: 'Next action', sortKey: 'nextActionDue' },
  { id: 'probability', label: 'Probability' },
  { id: 'discoveredAt', label: 'Discovered' },
];

/**
 * How a source kind prints. An unknown value prints itself rather than
 * vanishing - a source added to the puller and not to this map should look
 * odd, not look like nothing.
 */
const SOURCE_LABELS: Record<string, string> = {
  osm: 'OSM',
  google: 'Google',
  user: 'Manual',
};

/**
 * The kinds as filter options, from the map the cell prints with, so a kind
 * reads the same in the column and in the dropdown that narrows it. The
 * server lists the same three for the agent (LeadTableViewLogic).
 */
export const LEAD_SOURCE_OPTIONS: readonly { value: string; label: string }[] =
  Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }));

/**
 * The Sources cell: the KINDS behind this lead, not how many links there are.
 * The count is what the sort and the "Sources merged" filter work on, and it
 * is almost always 1, which is why the column shows these instead.
 */
export function leadSourceLabel(kinds: readonly string[] | null | undefined): string {
  if (!kinds?.length) return '—';
  return kinds.map(kind => SOURCE_LABELS[kind] ?? kind).join(' · ');
}

/** A next action whose date has passed. Both tables mark it the same way. */
export function leadActionOverdue(due: string | null | undefined): boolean {
  return due != null && new Date(due) < new Date();
}
